// components/Disc/DrawDisc.jsx
//
// Builds the layout tree the <Disc> renderer turns into SVG arcs. Every node
// ends up with four NORMALIZED polar coordinates (all fractions, 0..1):
//   x  = start angle      (fraction of the full circle; 0 = 12 o'clock)
//   dx = angular width    (fraction of the circle this node spans)
//   y  = inner radius     (fraction of the disc radius; 0 = center, 1 = edge)
//   dy = radial thickness (fraction of the disc radius)
// Disc.jsx scales these by 2π (angles) and the pixel radius to draw each arc.
//
// The tree mirrors the curriculum hierarchy by depth:
//   depth 0 = root (not drawn)
//   depth 1 = Area, 2 = Competency, 3 = Skill (the three visible rings).
// Each node also gets `alpha` (0..1 fill saturation = mastery) and counts used
// by hover popovers.
//
// Pipeline (drawMosaicArcs): build root → attach() sets depth/radius/colors →
// computeAssessedUsingScores() sets alpha/counts → layoutAngles() sets x/dx.

import { layoutAngles } from './discGeometry';
import { level3Mapper } from './DiscMappers';

// Radial bands, as fractions of the disc radius. Mosaico parity: two thin
// inner rings (Area, Competency) and one deep outer band in which each
// competency's skills are tiled as a GRID of cells (rows × columns, see
// layoutSkillGrid) rather than one thin ring of slivers — with the whole
// taxonomy drawn (361 skills) a single ring is unreadable. Mosaico's own
// bands in sqrt-radius terms are ≈ 0.20–0.26 / 0.26–0.33 / 0.39–0.99; these
// keep the inner rings a touch thicker so they stay hoverable.
const RINGS = {
  0: { y: 0.0, dy: 0.0 }, // root (not drawn)
  1: { y: 0.14, dy: 0.12 }, // Area
  2: { y: 0.27, dy: 0.13 }, // Competency
  3: { y: 0.44, dy: 0.54 }, // Skill cells (grid inside the competency wedge)
};
// Angular gap between cells in the same row, as a fraction of the circle.
const CELL_GAP = 0.0008;

/**
 * How a competency's N skills tile its wedge: an array of column counts, one
 * per row, innermost row first. Mosaico's hand-tuned table (level3Mapper)
 * covers N ≤ 12 and 16; any other N spreads over at most 4 rows as evenly as
 * possible, the longer rows outermost — the same shape the table produces
 * (e.g. 5 → [1, 1, 1, 2], 6 → [1, 1, 2, 2]).
 */
function rowPlan(n) {
  const table = level3Mapper[n];
  if (table) {
    const plan = [];
    for (let i = 0; i < n; i++) if (table[i].breakline) plan.push(table[i].columns);
    return plan;
  }
  const rows = Math.min(4, Math.max(1, n));
  const base = Math.floor(n / rows);
  const extra = n % rows;
  return Array.from({ length: rows }, (_, r) => base + (r >= rows - extra ? 1 : 0));
}

/**
 * Lays a competency's skills (depth 3) out as cells in the outer band:
 * rows share the band's thickness equally; within a row the cells split the
 * competency's angular span equally. Replaces the per-skill angular layout
 * for depth 3 only — Areas and Competencies keep their weighted wedges.
 */
function layoutSkillGrid(comp) {
  const kids = comp.children || [];
  if (!kids.length) return;
  const band = RINGS[3];
  const plan = rowPlan(kids.length);
  const dy = band.dy / plan.length;
  let i = 0;
  plan.forEach((cols, row) => {
    const gap = Math.min(CELL_GAP, comp.dx / (cols * 4));
    const dx = (comp.dx - gap * (cols - 1)) / cols;
    for (let c = 0; c < cols && i < kids.length; c++, i++) {
      const k = kids[i];
      k.y = band.y + row * dy;
      k.dy = dy;
      k.x = comp.x + c * (dx + gap);
      k.dx = dx;
    }
  });
}

function setRadial(node) {
  const m = RINGS[node.depth] || RINGS[0];
  node.y = m.y;
  node.dy = m.dy;
}
import { DEV_MIN } from '../../lib/discStats';

/**
 * Computes per-node "development" (mastery) and the fill alpha, bottom-up.
 *
 * Definitions (Mosaico parity):
 * - A specific skill (the leaves under a depth-3 Skill) is DEVELOPED when its
 *   averageScore >= DEV_MIN (from discStats; now 3, re-ruled 2026-07-31) — i.e.
 *   the class, on average, has moved past "developing" on it. NOTE: "developed"
 *   is NOT "proficient": proficient stays anchored at level 4 (the rubric; the
 *   emphasized ring-4 line in LevelRingsDisc). Do not conflate the two.
 * - A node's `alpha` (fill opacity, 0..1) = developed / total specific skills
 *   beneath it. So a fully-mastered branch is fully saturated; an unassessed
 *   one is alpha 0 → rendered white by Disc.jsx.
 * - Inner rings (Area, Competency) roll their counts up from descendants, so a
 *   ring's color intensity reflects the share of mastered specific skills in
 *   its whole subtree.
 *
 * `scores` is a flat list keyed by lv4ItemId (specific skill id); we index it
 * into scoreMap and look each leaf up by id. Also stamps _assessedCount /
 * _totalCount on each node for the hover popover.
 */
function computeAssessedUsingScores(root, scores) {
  const scoreMap = new Map();
  for (const s of scores || []) {
    if (!s?.lv4ItemId) continue;
    scoreMap.set(String(s.lv4ItemId), Number(s.averageScore ?? s.score ?? 0));
  }

  const MIN_DEVELOPED_SCORE = DEV_MIN;

  function rec(node) {
    if (!node) return { assessed: 0, total: 0, developed: 0 };

    // depth 3 = Skill; its children are specific skills
    if (node.depth === 3) {
      const kids = node.children || [];
      const total = kids.length;
      let assessed = 0;
      let developed = 0;

      for (const k of kids) {
        const scoreValue = scoreMap.get(String(k.id)) || k.averageScore || k.score || 0;
        const isAssessed = scoreValue > 0 || k.assessed === true;

        k.assessed = isAssessed;
        k.score = scoreValue;
        k.averageScore = scoreValue;

        if (isAssessed) assessed++;
        if (scoreValue >= MIN_DEVELOPED_SCORE) developed++;
      }

      node._assessedCount = assessed;
      node._totalCount = total;
      node.assessed = assessed > 0;
      node.childrenDevelopment = developed;
      node.totalLevel4 = total;

      // Mosaico logic: alpha = developed / total
      if (total === 0) {
        node.alpha = 0;
      } else {
        node.alpha = developed / total;
      }

      return { assessed, total, developed };
    }

    if (!node.children || node.children.length === 0) {
      node._assessedCount = 0;
      node._totalCount = 0;
      node.assessed = false;
      node.childrenDevelopment = 0;
      node.totalLevel4 = 0;
      node.alpha = 0;
      return { assessed: 0, total: 0, developed: 0 };
    }

    let assessed = 0;
    let total = 0;
    let developed = 0;
    for (const c of node.children) {
      const r = rec(c);
      assessed += r.assessed;
      total += r.total;
      developed += r.developed;
    }

    node._assessedCount = assessed;
    node._totalCount = total;
    node.assessed = assessed > 0;
    node.childrenDevelopment = developed;
    node.totalLevel4 = total;

    // Mosaico logic: alpha = childrenDevelopment / totalLevel4
    if (total === 0) {
      node.alpha = 0;
    } else {
      node.alpha = developed / total;
    }

    return { assessed, total, developed };
  }

  rec(root);
}

/**
 * Angular weight of a node = how much of the circle it should occupy.
 * Mosaico parity: a Competency's wedge is sized by its number of SKILLS (each
 * skill is one cell of the grid, whatever its descriptor count), and an Area
 * by the skills beneath it, so every cell across the disc has a comparable
 * width. (Min 1 so empty branches still get a sliver.)
 */
function nodeWeight(node) {
  if (!node) return 1;
  if (node.depth === 3) return 1;
  if (!node.children || node.children.length === 0) return 1;

  let sum = 0;
  for (const c of node.children) sum += nodeWeight(c);
  return Math.max(1, sum);
}

/**
 * Entry point: turn a curriculum tree + flat scores into a fully-laid-out disc
 * tree (each node carrying x/dx/y/dy/alpha + counts).
 *
 * Steps:
 *   1. Wrap the (kind-filtered) curriculum under a synthetic depth-0 root that
 *      spans the whole circle (x=0, dx=1).
 *   2. attach(): set depth + radial band on every node and inherit colors down
 *      (areas default to a base blue; children inherit their parent's color).
 *   3. computeAssessedUsingScores(): roll up mastery → alpha + counts.
 *   4. layoutAngles(): distribute angular spans so siblings tile their parent.
 * The remaining params (spotlight/filter*) are kept for signature compatibility
 * with the original Mosaico component and are currently unused.
 */
export const drawMosaicArcs = (
  curriculum,
  scores,
  kind,
  spotlightToWorked,
  filterByGrade,
  filterByCycleName,
  gapByLevel
) => {
  const root = {
    id: 'root',
    name: 'root',
    depth: 0,
    x: 0,
    dx: 1,
    y: 0,
    dy: 0,
    color: '#cbd5e1',
    alpha: 0,
    children: (curriculum || []).filter((x) => !kind || x.kind === kind),
    parent: null,
  };

  function attach(node, depth) {
    node.depth = depth;
    setRadial(node);

    if (node.children && node.children.length) {
      for (const c of node.children) {
        c.parent = node;
        // Ensure color inheritance
        if (!c.color) c.color = node.color;
        attach(c, depth + 1);
      }
    }
  }

  for (const area of root.children) {
    area.parent = root;
    if (!area.color) area.color = '#6C91BF';
    attach(area, 1);
  }

  // Compute assessed counts and alpha values
  computeAssessedUsingScores(root, scores || []);

  // Layout angles to form complete circle (Areas and Competencies weighted by
  // skill count — see nodeWeight), then re-tile each competency's skills as a
  // grid of cells in the outer band (Mosaico's "mosaic").
  root.x = 0;
  root.dx = 1;
  layoutAngles(root, nodeWeight, gapByLevel ?? 0.004);
  for (const area of root.children) {
    for (const comp of area.children || []) layoutSkillGrid(comp);
  }

  return root;
};
